import { ElementType, useCallback, useEffect, useState, DragEvent } from 'react';
import { useDragAndDropContext } from './partials/DragAndDropContext';
import { isNullOrUndefined } from '../../lib/typeGuards';

export function useDragAndDrop<T extends { id: string }>(
  id: string,
  move: (sourceId: string, targetId: string, dontSubmitWhileDragging?: boolean) => void,
  dragPreview: ElementType<T>,
  dragPreviewProperties?: T,
  submitChange?: (id: string) => void,
) {
  const { draggedItem, newMousePosition, setDraggedItem, resetDraggedItem } = useDragAndDropContext();
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    setIsDragging(id === draggedItem?.id);
  }, [draggedItem, id]);

  const onDragStart = useCallback(
    (ev: DragEvent<HTMLElement>) => {
      // disable dragImage bc. we render a custom dragLayer
      ev.dataTransfer?.setDragImage(new Image(), 0, 0);
      const rect = ev.currentTarget.getBoundingClientRect();
      setDraggedItem({ id, Preview: dragPreview, props: dragPreviewProperties, previewWidth: rect.width });
      newMousePosition({ x: ev.clientX, y: ev.clientY }, rect.width);
    },
    [dragPreview, setDraggedItem, dragPreviewProperties, id, newMousePosition],
  );

  const onDragEnd = useCallback(() => {
    if (draggedItem) {
      resetDraggedItem();
      if (typeof submitChange === 'function') {
        submitChange(id);
      }
    }
  }, [draggedItem, resetDraggedItem, id, submitChange]);

  const onDragOver = useCallback(
    (ev: DragEvent) => {
      if (draggedItem) {
        newMousePosition({ x: ev.clientX, y: ev.clientY }, draggedItem.previewWidth);
      }
    },
    [draggedItem, newMousePosition],
  );

  const onDragEnter = useCallback(() => {
    if (isNullOrUndefined(draggedItem)) {
      return;
    }
    if (id !== draggedItem.id) {
      move(draggedItem.id, id, true);
    }
  }, [draggedItem, id, move]);

  return {
    isDragging,
    onDragStart,
    onDragEnter,
    onDragOver,
    onDragEnd,
  };
}
